import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

export function SongList() {
  const { courseId } = useParams<{ courseId: string }>();
  const { course, isLoading } = useCourse(courseId || '');
  const { user } = useAuth();
  const [listenedSongs, setListenedSongs] = useState<Set<string>>(new Set());

  // Check which songs have been listened to (>80%)
  useEffect(() => {
    async function loadProgress() {
      if (!user?.id || !courseId) return;

      const { data } = await supabase
        .from('activity_log')
        .select('details')
        .eq('user_id', user.id)
        .eq('action', 'song_listen')
        .order('created_at', { ascending: false });

      if (data) {
        const ids = new Set<string>();
        for (const row of data) {
          const d = typeof row.details === 'string' ? JSON.parse(row.details) : row.details;
          if (d?.course_id === courseId && d?.song_id) {
            ids.add(d.song_id);
          }
        }
        setListenedSongs(ids);
      }
    }
    loadProgress();
  }, [user?.id, courseId]);

  if (isLoading) {
    return <div className="loading">Laden...</div>;
  }

  const songs = course?.songs?.filter(s => s.category === 'songs') || [];

  if (songs.length === 0) {
    return (
      <div className="error-state">
        <p>Keine Songs vorhanden</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurueck zum Kurs
        </Link>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', maxWidth: '800px', margin: '0 auto' }}>
      <Link to={`/course/${courseId}`} className="back-link">
        <ArrowLeft size={18} />
        Zurueck zum Kurs
      </Link>

      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>🎵</div>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#e8eaf6' }}>Songs</h1>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
          Lerne Englisch mit Musik
        </p>
      </div>

      <div className="course-sections">
        {songs.map(song => {
          const listened = listenedSongs.has(song.id);
          return (
            <Link
              key={song.id}
              to={`/course/${courseId}/songs/${song.id}`}
              className="section-card"
              style={{ borderLeft: '4px solid #e91e63' }}
            >
              <div className="section-icon" style={{ fontSize: '24px' }}>
                {song.icon}
              </div>
              <div className="section-info" style={{ flex: 1 }}>
                <h3>{song.title}</h3>
                <p>{song.description}</p>
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.4)',
                  marginTop: '2px',
                }}>
                  {song.vocabCount} Vokabeln
                  {listened && (
                    <span style={{ color: '#4caf50', marginLeft: '8px' }}>
                      gehoert
                    </span>
                  )}
                </p>
              </div>
              <ChevronRight size={18} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
